/**
 * FlowDirector
 * 
 * Orchestrates flow execution, managing the lifecycle of flows,
 * handling triggers, and coordinating engines.
 */

import { IFlowDirector } from '../types';
import {
  SquiggleFlow,
  TriggerContext,
  ExecutionContext,
} from '../types';
import { useSquiggleStore } from '../../../stores/squiggleStore';
import { RiveController } from '../engines/RiveController';
import { TrailEngine } from '../engines/TrailEngine';
import { TargetManager } from '../engines/TargetManager';
import { FlowRegistry } from './FlowRegistry';
import { StepExecutor } from './StepExecutor';
import { ConditionEvaluator } from './ConditionEvaluator';

export class FlowDirector implements IFlowDirector {
  private riveController: RiveController;
  private trailEngine: TrailEngine;
  private targetManager: TargetManager;
  private flowRegistry: FlowRegistry;
  private stepExecutor: StepExecutor;
  private conditionEvaluator: ConditionEvaluator;

  private activeFlow: SquiggleFlow | null = null;
  private currentStepIndex: number = 0;
  private abortController: AbortController | null = null;
  private currentRoute: string = '/';
  
  // Resolve functions for bubble interactions (signaling pattern)
  private bubbleNextResolver: (() => void) | null = null;
  private bubbleDismissResolver: (() => void) | null = null;

  constructor(
    riveController: RiveController,
    trailEngine: TrailEngine,
    targetManager: TargetManager,
    flowRegistry: FlowRegistry
  ) {
    this.riveController = riveController;
    this.trailEngine = trailEngine;
    this.targetManager = targetManager;
    this.flowRegistry = flowRegistry;
    
    this.conditionEvaluator = new ConditionEvaluator();
    this.stepExecutor = new StepExecutor(
      riveController,
      trailEngine,
      targetManager,
      this.conditionEvaluator
    );

    // Listen for squiggle events
    if (typeof window !== 'undefined') {
      window.addEventListener('squiggle:event', this.handleSquiggleEvent);
    }
  }

  /**
   * Start a flow by ID
   */
  async startFlow(flowId: string): Promise<void> {
    const flow = this.flowRegistry.get(flowId);
    if (!flow) {
      console.warn(`[FlowDirector] Flow not found: ${flowId}`);
      return;
    }

    // Check gating conditions
    if (!this.checkGating(flow)) {
      console.log(`[FlowDirector] Flow gated: ${flowId}`);
      return;
    }

    // If another flow is active, queue this one
    if (this.activeFlow) {
      useSquiggleStore.getState().queueFlow(flowId);
      return;
    }

    // Start the flow
    await this.executeFlow(flow);
  }

  /**
   * Check flow gating conditions
   */
  private checkGating(flow: SquiggleFlow): boolean {
    const store = useSquiggleStore.getState();
    const gating = flow.gating;

    if (!gating) return true;

    // Once per user
    if (gating.oncePerUser && store.hasCompletedFlow(flow.id)) {
      return false;
    }

    // Once per session
    if (gating.oncePerSession && store.hasRunThisSession(flow.id)) {
      return false;
    }

    // Cooldown
    if (gating.cooldownMs) {
      const completion = store.completedFlows[flow.id];
      if (completion) {
        const elapsed = Date.now() - completion.completedAt;
        if (elapsed < gating.cooldownMs) {
          return false;
        }
      }
    }

    // Max runs
    if (gating.maxRuns) {
      const completion = store.completedFlows[flow.id];
      if (completion && completion.runCount >= gating.maxRuns) {
        return false;
      }
    }

    // Prerequisites
    if (gating.prerequisites && gating.prerequisites.length > 0) {
      if (!this.conditionEvaluator.evaluateAll(gating.prerequisites)) {
        return false;
      }
    }

    return true;
  }

  /**
   * Execute a flow
   */
  private async executeFlow(flow: SquiggleFlow): Promise<void> {
    const store = useSquiggleStore.getState();

    // Set active flow
    this.activeFlow = flow;
    this.currentStepIndex = 0;
    this.abortController = new AbortController();

    store.setActiveFlow(flow.id);
    store.show();

    console.log(`[FlowDirector] Starting flow: ${flow.id}`);

    try {
      // Execute steps
      while (this.currentStepIndex < flow.steps.length) {
        // Check if paused
        while (store.flowPaused && !this.abortController.signal.aborted) {
          await this.delay(100);
        }

        // Check if aborted
        if (this.abortController.signal.aborted) {
          break;
        }

        const step = flow.steps[this.currentStepIndex];
        store.setCurrentStepIndex(this.currentStepIndex);

        // Create execution context with promise-based signaling
        const context: ExecutionContext = {
          flowId: flow.id,
          stepId: step.id,
          abortSignal: this.abortController.signal,
          waitForBubbleNext: () => this.createBubbleNextPromise(),
          waitForBubbleDismiss: () => this.createBubbleDismissPromise(),
        };

        // Execute step
        const result = await this.stepExecutor.execute(step, context);

        console.log(`[FlowDirector] Step ${step.id} result:`, result.status);

        // Handle result
        if (result.status === 'cancelled') {
          break;
        }

        // Determine next step
        if (result.nextStepId) {
          const nextIndex = flow.steps.findIndex((s) => s.id === result.nextStepId);
          if (nextIndex !== -1) {
            this.currentStepIndex = nextIndex;
          } else {
            this.currentStepIndex++;
          }
        } else {
          this.currentStepIndex++;
        }
      }

      // Flow completed
      this.handleFlowComplete('complete');
    } catch (error) {
      console.error(`[FlowDirector] Flow error:`, error);
      this.handleFlowComplete('interrupt');
    }
  }

  /**
   * Handle flow completion
   */
  private handleFlowComplete(reason: 'complete' | 'skip' | 'interrupt'): void {
    if (!this.activeFlow) return;

    const store = useSquiggleStore.getState();
    const flow = this.activeFlow;

    console.log(`[FlowDirector] Flow ${flow.id} ended: ${reason}`);

    // Execute onExit behavior
    if (flow.onExit) {
      if (flow.onExit.clearTrail) {
        this.trailEngine.setMode('clear');
      }

      if (flow.onExit.mood) {
        this.riveController.setMood(flow.onExit.mood);
        store.setMood(flow.onExit.mood);
      }

      if (flow.onExit.returnHome) {
        store.returnHome();
      }
    }

    // Mark as completed if not interrupted
    if (reason === 'complete') {
      store.markFlowCompleted(flow.id);
    }

    // Clean up
    store.hideBubble();
    store.setActiveFlow(null);
    this.activeFlow = null;
    this.abortController = null;

    // Check for queued flows
    const nextFlowId = store.dequeueFlow();
    if (nextFlowId) {
      this.startFlow(nextFlowId);
    }
  }

  /**
   * Pause the current flow
   */
  pauseFlow(): void {
    useSquiggleStore.getState().pauseFlow();
  }

  /**
   * Resume the current flow
   */
  resumeFlow(): void {
    useSquiggleStore.getState().resumeFlow();
  }

  /**
   * Stop the current flow
   */
  stopFlow(reason: 'complete' | 'skip' | 'interrupt'): void {
    if (this.abortController) {
      this.abortController.abort();
    }
    this.handleFlowComplete(reason);
  }

  /**
   * Check if a flow is active
   */
  isFlowActive(): boolean {
    return this.activeFlow !== null;
  }

  /**
   * Get active flow ID
   */
  getActiveFlowId(): string | null {
    return this.activeFlow?.id ?? null;
  }

  /**
   * Handle route changes
   */
  handleRouteChange(path: string): void {
    this.currentRoute = path;
    this.conditionEvaluator.setRoute(path);

    // Check if current flow should be cancelled
    if (this.activeFlow) {
      const currentStep = this.activeFlow.steps[this.currentStepIndex];
      if (currentStep?.cancelOnRouteChange) {
        this.stopFlow('interrupt');
        return;
      }
    }

    // Check for triggered flows
    const context: TriggerContext = {
      route: path,
    };

    const matchingFlows = this.flowRegistry.findByTrigger(
      context,
      this.conditionEvaluator
    );

    if (matchingFlows.length > 0) {
      // Start highest priority flow
      this.startFlow(matchingFlows[0].id);
    }
  }

  /**
   * Handle custom events
   */
  handleEvent(name: string, payload?: unknown): void {
    const context: TriggerContext = {
      route: this.currentRoute,
      eventName: name,
      eventPayload: payload,
    };

    const matchingFlows = this.flowRegistry.findByTrigger(
      context,
      this.conditionEvaluator
    );

    if (matchingFlows.length > 0) {
      this.startFlow(matchingFlows[0].id);
    }
  }

  /**
   * Dispatch a custom event to trigger flows
   */
  dispatch(event: string, payload?: unknown): void {
    window.dispatchEvent(
      new CustomEvent('squiggle:event', {
        detail: { name: event, payload },
      })
    );
  }

  /**
   * Handle squiggle events from window
   */
  private handleSquiggleEvent = (event: Event): void => {
    const customEvent = event as CustomEvent;
    const { name, payload } = customEvent.detail || {};
    if (name) {
      this.handleEvent(name, payload);
    }
  };

  /**
   * Create a promise that resolves when bubble next is triggered
   */
  private createBubbleNextPromise(): Promise<void> {
    return new Promise((resolve) => {
      this.bubbleNextResolver = resolve;
    });
  }

  /**
   * Create a promise that resolves when bubble dismiss is triggered
   */
  private createBubbleDismissPromise(): Promise<void> {
    return new Promise((resolve) => {
      this.bubbleDismissResolver = resolve;
    });
  }

  /**
   * Trigger bubble next - resolves the waiting promise
   */
  triggerBubbleNext(): void {
    if (this.bubbleNextResolver) {
      this.bubbleNextResolver();
      this.bubbleNextResolver = null;
    }
  }

  /**
   * Trigger bubble dismiss - resolves the waiting promise and stops flow
   */
  triggerBubbleDismiss(): void {
    if (this.bubbleDismissResolver) {
      this.bubbleDismissResolver();
      this.bubbleDismissResolver = null;
    }
    this.stopFlow('skip');
  }

  /**
   * Helper: delay for ms
   */
  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  /**
   * Clean up
   */
  dispose(): void {
    if (typeof window !== 'undefined') {
      window.removeEventListener('squiggle:event', this.handleSquiggleEvent);
    }

    if (this.abortController) {
      this.abortController.abort();
    }
  }
}

export default FlowDirector;
